import { useEffect, useState } from 'preact/hooks';

/** A tiny observable value with a Preact hook. */
export interface Signal<T> {
	get(): T;
	set(v: T): void;
	update(fn: (v: T) => T): void;
	subscribe(fn: (v: T) => void): () => void;
	use(): T;
}

export function signal<T>(initial: T): Signal<T> {
	let value = initial;
	const subs = new Set<(v: T) => void>();
	const s: Signal<T> = {
		get: () => value,
		set(v) {
			value = v;
			for (const f of [...subs]) f(v);
		},
		update(fn) {
			s.set(fn(value));
		},
		subscribe(fn) {
			subs.add(fn);
			return () => subs.delete(fn);
		},
		use() {
			const [v, setV] = useState(value);
			useEffect(() => {
				setV(value);
				return s.subscribe((x) => setV(() => x));
			}, []);
			return v;
		},
	};
	return s;
}
